import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { toastApiError } from "@/lib/apiError";
import { openPaystackPopup } from "@/lib/paystackInline";
import {
  ArrowLeft, CheckCircle, Sparkle, Clock, Lightning, ShieldCheck,
} from "@phosphor-icons/react";
import David from "@/components/David";

const C = {
  bg: "#000",
  ink: "#0A2826",
  inkSoft: "#103532",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

export default function CreditsPacks() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [currency, setCurrency] = useState("KES");
  const [buying, setBuying] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: packs }, { data: me }] = await Promise.all([
          api.get("/credits/packs"),
          api.get("/credits/me"),
        ]);
        setData(packs);
        setSummary(me);
      } catch {
        toast.error("Could not load credits page");
      }
    })();
  }, []);

  const buy = async (packId) => {
    setBuying(packId);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/credits/checkout", {
        pack_id: packId,
        currency,
        callback_url: callbackUrl,
      });
      // Iter83: prefer Paystack Inline popup so the founder stays on the
      // page. Falls back to full-page redirect if the inline script is
      // blocked / offline. On success we jump to the callback page so
      // the existing verify-and-award-credits flow runs unchanged.
      await openPaystackPopup({
        accessCode: data.access_code,
        publicKey: data.public_key,
        reference: data.reference,
        authorizationUrl: data.authorization_url,
        onSuccess: () => {
          window.location.href = `${callbackUrl}?reference=${encodeURIComponent(data.reference)}`;
        },
        onClose: () => {
          setBuying("");
          toast.info("Checkout closed. You can try again anytime.");
        },
      });
    } catch (e) {
      toastApiError(e, "Could not start checkout", {
        context: { action: "credits_checkout", pack_id: packId, currency },
      });
      setBuying("");
    }
  };

  if (!data || !summary) {
    return (
      <div className="min-h-screen grid place-items-center text-white/50" style={{ backgroundColor: C.bg }}>
        Loading credits…
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bg, fontFamily: "Manrope, sans-serif" }} data-testid="credits-page">
      <header className="border-b" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/dashboard" data-testid="credits-back-link"
            className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white transition-colors">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          <div className="inline-flex items-center gap-2 text-[12px] font-mono-num rounded-full px-3 py-1.5"
            style={{ backgroundColor: `${C.orange}15`, border: `1px solid ${C.orange}40`, color: C.orange }}
            data-testid="credits-current-balance">
            <Sparkle size={11} weight="fill" />
            <span className="font-extrabold tabular-nums">{summary.balance}</span>
            <span className="opacity-70">credits available</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: C.orange }}>
          Pitch deck credits
        </div>
        <h1 className="font-display font-black mt-2" style={{ fontSize: "3.2rem", lineHeight: 1.05, letterSpacing: "-0.03em" }}>
          Top up your <span style={{ color: C.tealSoft }}>creative fuel</span>.
        </h1>
        <p className="text-white/65 text-base max-w-2xl mt-4 leading-relaxed">
          Credits power the AI inside the pitch deck builder. Use them to draft slides, import existing decks,
          and generate the final funder-ready PowerPoint. They never disappear — credits stay live for 12 months from purchase.
        </p>

        {/* Cost transparency strip */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
          <CostTile label="AI draft a slide" cost={data.costs.ai_draft_per_slide} accent={C.tealSoft} icon={Lightning} />
          <CostTile label="Import existing deck" cost={data.costs.upload_existing_deck} accent={C.tealSoft} icon={Lightning} />
          <CostTile label="Create your pitch deck (PPTX)" cost={data.costs.create_pitch_deck} accent={C.orange} icon={Sparkle} />
          <CostTile label="Signup bonus" cost={`${data.signup_bonus} free`} accent="#9AE6B4" icon={CheckCircle} />
        </div>

        {/* Currency — merchant is KES-only for now */}
        <div className="mt-12 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45">Pay in</span>
          <div className="inline-flex rounded-md overflow-hidden border" style={{ borderColor: "rgba(123,211,206,0.25)" }}
               data-testid="currency-toggle-KES">
            <div className="text-xs font-bold px-4 py-2" style={{ backgroundColor: C.teal, color: "#fff" }}>KES</div>
          </div>
          <span className="text-[11px] text-white/40">
            Charged in Kenyan Shillings via Paystack.
          </span>
        </div>

        {/* Packs */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
          {data.packs.map((p) => {
            const price = p.prices.find((pr) => pr.currency === currency) || p.prices[0];
            const isHighlighted = p.highlighted;
            return (
              <div key={p.id} className="rounded-2xl border p-7 relative overflow-hidden transition-all hover:-translate-y-0.5"
                style={{
                  borderColor: isHighlighted ? `${C.orange}55` : "rgba(123,211,206,0.18)",
                  backgroundColor: isHighlighted ? "rgba(245,158,11,0.04)" : C.ink,
                }}
                data-testid={`credit-pack-${p.id}`}>
                {isHighlighted && (
                  <div className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.25em] font-bold px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: C.orange, color: "#000" }}>
                    Most popular
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2"
                  style={{ color: isHighlighted ? C.orange : C.tealSoft }}>
                  {p.label}
                </div>
                <div className="flex items-baseline gap-2">
                  <div className="font-display font-black text-5xl" style={{ letterSpacing: "-0.03em" }}>
                    {p.credits}
                  </div>
                  <div className="text-white/55 font-semibold">credits</div>
                </div>
                <div className="font-display font-bold text-2xl mt-4" data-testid={`credit-pack-${p.id}-price`}>
                  {price.display}
                </div>
                <div className="text-white/55 text-sm mt-2 leading-relaxed">{p.tagline}</div>

                <ul className="mt-5 space-y-2 text-[13px] text-white/70">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" style={{ color: C.tealSoft }} className="mt-0.5 shrink-0" />
                    <span>Use across the entire deck — drafts, imports, exports.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock size={14} weight="fill" style={{ color: C.tealSoft }} className="mt-0.5 shrink-0" />
                    <span>Credits stay live for 12 months from purchase.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ShieldCheck size={14} weight="fill" style={{ color: C.tealSoft }} className="mt-0.5 shrink-0" />
                    <span>Secured by Paystack · {currency === "KES" ? "M-Pesa & card supported" : "Visa, Mastercard, Verve"}.</span>
                  </li>
                </ul>

                <button onClick={() => buy(p.id)} disabled={buying === p.id}
                  data-testid={`buy-pack-${p.id}`}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold transition-colors disabled:opacity-60"
                  style={{
                    backgroundColor: isHighlighted ? C.orange : C.teal,
                    color: isHighlighted ? "#000" : "#fff",
                  }}>
                  {buying === p.id ? "Redirecting to Paystack…" : `Buy ${p.credits} credits · ${price.display}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-[11px] text-white/40 max-w-2xl leading-relaxed">
          We never store your card. All payments are processed by Paystack on encrypted infrastructure. Credits are
          delivered automatically the moment your payment clears — usually a few seconds, never more than a minute.
        </p>
      </main>
      <David />
    </div>
  );
}

function CostTile({ label, cost, accent, icon: Icon }) {
  return (
    <div className="rounded-lg border p-3"
      style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.3)" }}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white/50">
        <Icon size={11} weight="fill" style={{ color: accent }} />
        {label}
      </div>
      <div className="font-display font-extrabold mt-1 text-xl" style={{ color: accent, letterSpacing: "-0.02em" }}>
        {cost}{typeof cost === "number" ? " cr" : ""}
      </div>
    </div>
  );
}
